/**
 * Team-blok "Uren nog niet bevestigd" (Spoor 2, Verlof correctie — Story 4.3)
 *
 * Staat onder de goedkeuringslijst en lijkt daarop, maar is het
 * tegenovergestelde: **er valt niets te beslissen.** De leidinggevende mag dit
 * negeren; HR heeft het vangnet (de pagina *Verlof afsluiten*). Vandaar
 * grijs-neutraal zonder accentkleur, geen goedkeur- of bevestigknop, en geen
 * doorklik op een naam — dat zou iets beloven wat er niet is.
 *
 * Eén regel eronder rechtvaardigt het blok: *"Deze worden vanzelf definitief
 * na …"*. Zonder die datum weet niemand hoe lang hij nog iets kan doen.
 *
 * PRIVACY. Wat dit blok toont komt uit `TeamOnbevestigdeUrenRegel`, en die
 * vorm draagt geen verlofsoort, geen boekingstoestand en geen foutmelding.
 * Dat is geen vormkwestie: bij verlof dat met opzet nooit in de
 * loonadministratie komt zou de soortnaam de aanleiding verraden — een ziek
 * kind, een partner in het ziekenhuis. De server houdt die rijen al helemaal
 * buiten dit antwoord; dit bestand voegt er niets aan toe en leidt niets af
 * uit een boekingstoestand.
 */

import { useState } from 'react';
import type { OwnLeaveHistoryEntry, TeamOnbevestigdeUrenRegel } from '@functieloonhuis/shared';
import { useOnbevestigdeUren } from './hooks/useOnbevestigdeUren';
import { LeaveCorrectionSheet } from './LeaveCorrectionSheet';
import { formatNlUren } from './urenFormat';

/** UX-DR14: "6 aug" — dag + korte maandnaam, geen jaartal. */
function formatKorteDatum(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** UX-DR14: "31 augustus" — dag + volledige maandnaam, geen jaartal. */
function formatLangeDatum(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/**
 * De correctie-sheet uit story 2.4 verwacht een `OwnLeaveHistoryEntry`. De
 * smalle teamvorm draagt met opzet minder velden, dus wat de sheet niet leest
 * blijft hier leeg — de verloftypenaam nadrukkelijk óók, zodat er langs deze
 * weg niets binnenkomt wat de regelvorm juist buiten houdt.
 */
function alsHistorieRegel(regel: TeamOnbevestigdeUrenRegel): OwnLeaveHistoryEntry {
  return {
    id: regel.id,
    leaveType: 0,
    leaveTypeName: '',
    startDate: regel.startDate,
    endDate: regel.endDate,
    startPart: 'hele_dag',
    endPart: 'hele_dag',
    hours: regel.hours,
    description: null,
    requestStatus: 'goedgekeurd',
    decisionReason: null,
    syncStatus: 'synced',
    submittedAt: '',
    startTime: regel.startTime,
    endTime: regel.endTime,
    correctieTermijnEinde: regel.definitiefOp,
    correctieTermijnVerstreken: false,
  } as OwnLeaveHistoryEntry;
}

export function TeamOnbevestigdeUrenBlok() {
  const { data, isError } = useOnbevestigdeUren();
  const [correctieDoel, setCorrectieDoel] = useState<TeamOnbevestigdeUrenRegel | null>(null);

  const regels = data?.data.regels ?? [];
  const definitiefOp = data?.data.definitiefOp ?? null;

  // Geen inhoud, geen blok (AC-6). En bij een weigering evenmin een
  // foutmelding: wie de goedkeuringslijst wél mag zien maar dit blok niet,
  // hoort geen fout te krijgen op iets wat hij niet gevraagd heeft.
  if (isError || regels.length === 0) return null;

  return (
    <>
      <section
        data-testid="team-onbevestigd-blok"
        aria-label={`Uren nog niet bevestigd, ${regels.length}`}
        className="mt-8 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4"
      >
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-[#1E293B]">Uren nog niet bevestigd</h2>
          <span className="inline-block rounded-full border border-[#E2E8F0] bg-white px-2 py-0.5 text-xs font-medium text-[#64748B]">
            {regels.length}
          </span>
        </div>

        <ul className="mt-3 divide-y divide-[#E2E8F0]">
          {regels.map((regel) => (
            <li key={regel.id} className="flex items-center justify-between gap-3 py-2">
              <p className="text-sm text-[#1E293B]">
                <span>{regel.employeeNaam}</span>
                <span aria-hidden="true"> · </span>
                <span>{formatKorteDatum(regel.startDate)}</span>
                {regel.startTime !== null && regel.endTime !== null && (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span>
                      {regel.startTime}–{regel.endTime}
                    </span>
                  </>
                )}
                <span aria-hidden="true"> · </span>
                <span>{formatNlUren(regel.hours)} uur</span>
              </p>
              {/* Eén pagina met drie identieke knoppen "Aanpassen" is met een
                  schermlezer niet te bedienen (UX-DR15): de naam hoort erin. */}
              <button
                type="button"
                aria-label={`Aanpassen — ${regel.employeeNaam}`}
                onClick={() => setCorrectieDoel(regel)}
                className="shrink-0 rounded-md border border-[#E2E8F0] bg-white px-3 py-1 text-sm text-[#1E293B]"
              >
                Aanpassen
              </button>
            </li>
          ))}
        </ul>

        {definitiefOp !== null && (
          <p className="mt-3 text-xs text-[#64748B]">
            {`Deze worden vanzelf definitief na ${formatLangeDatum(definitiefOp)}.`}
          </p>
        )}
      </section>

      {correctieDoel && (
        <LeaveCorrectionSheet
          isOpen
          onClose={() => setCorrectieDoel(null)}
          onSubmitted={() => setCorrectieDoel(null)}
          historyEntry={alsHistorieRegel(correctieDoel)}
          employeeName={correctieDoel.employeeNaam}
        />
      )}
    </>
  );
}
